import { useMemo } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerHeader } from "common/components/ui/Drawer";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import { startAccountsBulkAction } from "../../utils";

import SeatPurchaseImage from "assets/images/seat-purchase.png";

interface AssignSeatsProps {
  selectedAccounts: WorkspaceAccount[];
  onBack: () => void;
}

export default function AssignSeats({
  selectedAccounts,
  onBack,
}: AssignSeatsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { assignLicense, isAssigningLicense } = useLicenseMutations();

  const rentedAccounts = useMemo(
    () => selectedAccounts.filter(({ type }) => type === "rental"),
    [selectedAccounts],
  );

  const { accountsWithSeats, accountsWithoutSeats } = useMemo(() => {
    const withSeats: WorkspaceAccount[] = [];
    const withoutSeats: WorkspaceAccount[] = [];

    selectedAccounts.forEach((account) => {
      if (account.license || account.type === "rental") {
        withSeats.push(account);
      } else {
        withoutSeats.push(account);
      }
    });

    return { accountsWithSeats: withSeats, accountsWithoutSeats: withoutSeats };
  }, [selectedAccounts]);

  function assignSeats() {
    startAccountsBulkAction(
      (accountId, abortSignal) => assignLicense({ accountId, abortSignal }),
      accountsWithoutSeats,
      (finishedItemsCount) =>
        finishedItemsCount === accountsWithoutSeats.length
          ? `Assignment complete`
          : `Assigning ${finishedItemsCount}/${accountsWithoutSeats.length} seats`,
      true,
    );
    onBack();
  }

  const title = "Assign seats";
  let description: string;

  if (accountsWithoutSeats.length === 0) {
    description = "All selected LinkedIn accounts already have seats assigned";
  } else if (accountsWithSeats.length > 0) {
    const withSeatsText =
      accountsWithSeats.length === 1
        ? `${accountsWithSeats[0].full_name} already has seat assigned`
        : `${accountsWithSeats.length} accounts already have seats assigned`;

    const withoutSeatsText =
      accountsWithoutSeats.length === 1
        ? `a seat to ${accountsWithoutSeats[0].full_name}`
        : `seats to the remaining ${accountsWithoutSeats.length} accounts`;

    description = `${withSeatsText}, do you want to assign ${withoutSeatsText}?`;
  } else {
    description =
      selectedAccounts.length === 1
        ? `Assign a seat to ${selectedAccounts[0].full_name}?`
        : `Assign seats to ${selectedAccounts.length} accounts?`;
  }

  return (
    <>
      {isDesktop ? (
        <>
          <img
            src={SeatPurchaseImage}
            alt=""
            width={280}
            height={195}
            className="mx-auto"
          />
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="mb-4">{description}</DialogDescription>
        </>
      ) : (
        <DrawerHeader title={title} subtitle={description} onBack={onBack} />
      )}

      {rentedAccounts.length > 0 && (
        <Alert variant="warning" className="my-4">
          Rented LinkedIn accounts always have seats
        </Alert>
      )}

      <div className="justify-between mt-4 flex gap-x-2.5">
        <Button
          variant="secondary-black"
          onClick={onBack}
          className="lg:flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="gradient"
          onClick={assignSeats}
          className="lg:flex-1"
          isLoading={isAssigningLicense}
          disabled={!accountsWithoutSeats.length}
        >
          Assign
        </Button>
      </div>
    </>
  );
}
